import React, { useEffect, useState } from "react";
import { Star, Quote, ExternalLink, Award, ThumbsUp, RefreshCw } from "lucide-react";
import { useSettings } from "../../lib/useSettings";
import { Button } from "../ui/button";
import { api } from "../../lib/api";
import { openGoogleReview, normalizeGoogleReviewUrl, buildReviewUrlFromPlaceId, isInAppBrowser } from "../../lib/googleReview";

export default function TestimonialsSection() {
  const { settings: s } = useSettings();
  const [data, setData] = useState({ reviews: [], meta: {} });

  useEffect(() => {
    api.get("/google-reviews").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const reviews = data.reviews || [];
  const meta = data.meta || {};
  const reviewsCount = meta.count || reviews.length || s?.reviews_count || 0;
  const avg = meta.avg || s?.reviews_average || 5.0;
  const since = s?.reviews_since_year || 2022;

  const businessTitle = s?.google_business_title || s?.company_name || "Kloub'Air Plomberie";
  const googleSearchUrl = `https://www.google.com/search?q=${encodeURIComponent(businessTitle + " avis")}`;
  // Priority: explicit review_url > placeId-built review URL > search fallback
  const googleReviewUrl = normalizeGoogleReviewUrl(s?.google_review_url)
    || buildReviewUrlFromPlaceId(s?.google_place_id)
    || googleSearchUrl;

  const lastSyncLabel = meta.last_sync
    ? new Date(meta.last_sync).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <section className="py-20 md:py-28 bg-[hsl(var(--background))]" data-testid="testimonials-section">
      <div className="relative max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="text-xs md:text-sm uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold mb-3">
              Avis Google vérifiés
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-[color:var(--brand-navy)] leading-tight max-w-2xl">
              Qui de mieux que <em className="not-italic brand-gradient-text">nos clients</em> pour en parler ?
            </h2>
            <p className="mt-4 text-[hsl(var(--muted-foreground))] text-base max-w-xl">
              Avec Kloub'Air Plomberie, vous êtes assuré d'un travail de qualité, professionnel et soigné — comme l'attestent nos clients depuis {since}.
            </p>
          </div>

          <a
            href={googleSearchUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 p-4 md:p-5 rounded-2xl bg-white border border-[hsl(var(--border))] shadow-soft hover:shadow-hover hover:border-[color:var(--brand-sky)] transition-all"
            data-testid="google-reviews-badge"
          >
            <GoogleG className="w-10 h-10 flex-shrink-0" />
            <div>
              <div className="flex items-center gap-1 mb-0.5">
                <span className="font-display font-bold text-xl text-[color:var(--brand-navy)]">{avg}</span>
                <span className="flex gap-0.5 ml-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className={`w-4 h-4 ${i < Math.round(avg) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`} />
                  ))}
                </span>
              </div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">
                <strong className="text-[color:var(--brand-navy)]">{reviewsCount} avis Google</strong> vérifiés
              </div>
            </div>
            <ExternalLink className="w-4 h-4 text-[hsl(var(--muted-foreground))] flex-shrink-0" />
          </a>
        </div>

        {/* Real Google reviews */}
        {reviews.length > 0 ? (
          <div className="grid md:grid-cols-3 gap-5 mb-10">
            {reviews.slice(0, 6).map((t, i) => (
              <article
                key={t.id || i}
                className="bg-white rounded-3xl p-6 md:p-7 shadow-soft relative border border-[hsl(var(--border))] flex flex-col"
                data-testid={`google-review-${i}`}
              >
                <Quote className="absolute top-6 right-6 w-7 h-7 text-[color:var(--brand-sky)]/15" />
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, j) => (
                      <Star
                        key={j}
                        className={`w-4 h-4 ${j < (t.rating || 5) ? "fill-yellow-400 text-yellow-400" : "text-gray-200"}`}
                      />
                    ))}
                  </div>
                  <GoogleG className="w-4 h-4 opacity-70" />
                </div>
                <p className="text-[hsl(var(--foreground))] text-[15px] leading-relaxed mb-6 font-body flex-1 line-clamp-6">
                  {t.text}
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-[hsl(var(--border))]">
                  {t.avatar ? (
                    <img src={t.avatar} alt={t.author} className="w-10 h-10 rounded-full object-cover" loading="lazy" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[color:var(--brand-navy)] text-white flex items-center justify-center font-display font-bold text-sm">
                      {(t.author || "?").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-[color:var(--brand-navy)] truncate flex items-center gap-1.5">
                      {t.author}
                      {t.local_guide && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-bold uppercase">Local Guide</span>
                      )}
                    </div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{t.date}</div>
                  </div>
                  {t.link && (
                    <a href={t.link} target="_blank" rel="noopener noreferrer" className="text-[hsl(var(--muted-foreground))] hover:text-[color:var(--brand-sky)]" aria-label="Voir sur Google">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mb-10 p-8 rounded-3xl bg-white border border-dashed border-[hsl(var(--border))] text-center">
            <RefreshCw className="w-8 h-8 mx-auto text-[hsl(var(--muted-foreground))] mb-3" />
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Synchronisation des avis Google en cours…</p>
          </div>
        )}

        {/* Trust strip */}
        <div className="grid md:grid-cols-3 gap-4 mt-4">
          <div className="bg-white rounded-2xl p-6 border border-[hsl(var(--border))] flex items-start gap-4">
            <Award className="w-9 h-9 p-2 rounded-xl bg-[color:var(--brand-sky)]/10 text-[color:var(--brand-sky)] flex-shrink-0" />
            <div>
              <div className="font-display font-bold text-[color:var(--brand-navy)]">10 ans d'expérience</div>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">François Kloubert et son équipe au service des Bouches-du-Rhône depuis plus d'une décennie.</p>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-[hsl(var(--border))] flex items-start gap-4">
            <ThumbsUp className="w-9 h-9 p-2 rounded-xl bg-[color:var(--brand-sky)]/10 text-[color:var(--brand-sky)] flex-shrink-0" />
            <div>
              <div className="font-display font-bold text-[color:var(--brand-navy)]">Marques premium</div>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Partenaires DAIKIN, MITSUBISHI Electric, HEIWA et Hitachi — des leaders mondiaux choisis pour leur fiabilité.</p>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-[hsl(var(--border))] flex items-start gap-4">
            <GoogleG className="w-9 h-9 p-2 rounded-xl bg-[color:var(--brand-sky)]/10 flex-shrink-0" />
            <div>
              <div className="font-display font-bold text-[color:var(--brand-navy)]">{avg}/5 sur Google</div>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{reviewsCount} avis clients vérifiés{lastSyncLabel ? ` · synchronisé le ${lastSyncLabel}` : ""}.</p>
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-col md:flex-row gap-3 items-center justify-center">
          <a href={googleSearchUrl} target="_blank" rel="noopener noreferrer" data-testid="view-all-reviews">
            <Button variant="outline" className="rounded-full gap-2 h-11">
              <GoogleG className="w-4 h-4" />
              Voir les avis sur Google
              <ExternalLink className="w-3.5 h-3.5" />
            </Button>
          </a>
          <a
            href={googleReviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="leave-review-cta"
            onClick={(e) => {
              // In WebView (Instagram, Facebook, TikTok…), Google login hangs on a blank page.
              // Intercept ONLY in those cases to copy the URL and show guidance.
              // In normal browsers, let the native <a target="_blank"> open the new tab — this
              // avoids popup blockers that fire on programmatic window.open().
              if (isInAppBrowser()) {
                e.preventDefault();
                openGoogleReview(googleReviewUrl, googleSearchUrl);
              }
            }}
          >
            <Button className="rounded-full gap-2 h-11 bg-[color:var(--brand-sky)] hover:bg-[color:var(--brand-sky)]/90 text-white">
              <Star className="w-4 h-4 fill-current" />
              Laisser un avis
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
}

function GoogleG({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.55c2.08-1.92 3.29-4.74 3.29-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.55-2.76c-.98.66-2.24 1.06-3.73 1.06-2.87 0-5.3-1.94-6.17-4.55H2.18v2.85A10.99 10.99 0 0 0 12 23z"/>
      <path fill="#FBBC05" d="M5.83 14.09a6.58 6.58 0 0 1 0-4.18V7.06H2.18a10.99 10.99 0 0 0 0 9.88l3.65-2.85z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.1 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.65 2.85C6.7 7.32 9.13 5.38 12 5.38z"/>
    </svg>
  );
}
